import { useMemo } from "react";
import { calculateNextReviewDueDate } from "../../shared/hooks/useNextReviewDate";

export const useSystemGrouping = (criticalSystems, employees, showArchivedSystems) => {
  const getAdminName = (email, employeesList) => {
    if (!email) return null;
    const employee = employeesList.find(emp => {
      const primaryEmail = emp.emails?.find((em) => em.type === "work")?.email || emp.emails?.[0]?.email || "";
      return primaryEmail.toLowerCase() === email.toLowerCase();
    });
    return employee ? (employee.name || `${employee.first_name || ''} ${employee.last_name || ''}`.trim()) : email;
  };

  const groupedSystems = useMemo(() => {
    const groups = new Map();
    criticalSystems.forEach((system) => {
      const groupKey = system.name;
      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey).push(system);
    });
    
    const result = [];
    groups.forEach((allAccounts, groupKey) => {
      const accountsForAggregation = allAccounts.filter(acc => {
        const isActive = acc.is_review_active !== false;
        return showArchivedSystems ? !isActive : isActive;
      });

      const manualCount = accountsForAggregation.filter(acc => (acc.integration_type || '').toUpperCase() === 'MANUAL').length;
      const autoCount = accountsForAggregation.filter(acc => (acc.integration_type || '').toUpperCase() !== 'MANUAL').length;
      const criticalCount = accountsForAggregation.filter(acc => acc.is_critical).length;
      const standardCount = accountsForAggregation.length - criticalCount;
      
      const uniqueAdminEmails = [...new Set(accountsForAggregation.map(acc => acc.system_admin).filter(Boolean))];
      const adminNames = uniqueAdminEmails.map(email => getAdminName(email, employees));
      const adminCount = adminNames.length;
      
      const adminDisplay = adminCount > 2 
        ? `${adminNames.slice(0, 2).join(', ')} +${adminCount - 2}`
        : adminCount > 0 
          ? adminNames.join(', ')
          : '';
      
      const reviewDates = accountsForAggregation.map(acc => acc.last_reviewed_at).filter(Boolean);
      const hasMixedDates = new Set(reviewDates).size > 1;
      
      const nextReviewDates = accountsForAggregation
        .map(acc => calculateNextReviewDueDate(acc.last_reviewed_at, acc.review_cycle, acc.created_at))
        .filter(Boolean)
        .sort((a, b) => a - b);
      const earliestDueDate = nextReviewDates.length > 0 ? nextReviewDates[0] : null;
      
      const groupHeader = {
        id: `group_${groupKey}`,
        name: groupKey,
        isGroupHeader: true,
        groupKey,
        accountCount: accountsForAggregation.length,
        groupAccounts: allAccounts,
        integration_type: manualCount > 0 && autoCount > 0 ? 'MIXED' : (manualCount > 0 ? 'MANUAL' : 'AUTOMATED'),
        manualCount,
        autoCount,
        is_critical: criticalCount > 0,
        criticalCount,
        standardCount,
        system_admin: uniqueAdminEmails[0] || null,
        adminDisplay,
        adminCount,
        adminNames,
        last_reviewed_at: hasMixedDates ? 'MIXED' : (reviewDates[0] || null),
        hasMixedDates,
        earliestDueDate,
        type: allAccounts[0]?.type,
        review_cycle: allAccounts[0]?.review_cycle,
        created_at: allAccounts[0]?.created_at,
        is_review_active: accountsForAggregation.some(acc => acc.is_review_active !== false),
        account_label: null,
      };
      
      result.push(groupHeader);
      
      allAccounts.forEach((account) => {
        result.push({
          ...account,
          isGroupChild: true,
          groupKey,
          accountCount: accountsForAggregation.length,
          groupAccounts: allAccounts,
        });
      });
    });
    
    return result;
  }, [criticalSystems, employees, showArchivedSystems]);

  return groupedSystems;
};

